import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

/**
 * iter-232.J (RC-1) — Persistent, top-of-app banner shown whenever the
 * current session is an impersonation. Provides two surfaces:
 *
 *   1. Active state — "You are viewing as <vendor>. [Return to Admin]"
 *      (always visible during impersonation; the only path that
 *      cleanly restores the admin token to ALL storage tiers).
 *
 *   2. Expired state — surfaced when AuthContext dispatches the
 *      `impersonation:expired` event (the impersonation access token
 *      hit 401 and we refused to use the admin refresh-token to mint
 *      a new admin access token — the bug that destroyed Meenga's
 *      vendor portal session on production).
 *
 * Sits above all routes; non-blocking. No silent navigation away —
 * the admin clicks the CTA explicitly so they always know which
 * identity they're operating under.
 */
export default function ImpersonationBanner() {
  const { user, endImpersonation } = useAuth();
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // eslint-disable-next-line no-console
      console.warn('[ImpersonationBanner] expired event', e?.detail);
      setExpired(true);
    };
    window.addEventListener('impersonation:expired', handler);
    return () => window.removeEventListener('impersonation:expired', handler);
  }, []);

  const isImpersonating = !!user?.is_impersonation;
  if (!isImpersonating && !expired) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[100] border-b shadow-sm ${
        expired
          ? 'bg-red-50 border-red-300 text-red-900'
          : 'bg-amber-50 border-amber-300 text-amber-900'
      }`}
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span aria-hidden="true">{expired ? '⚠️' : '👤'}</span>
          {expired ? (
            <span data-testid="impersonation-banner-expired-text">
              <strong>Impersonation session expired.</strong> Vendor token is
              no longer valid. Return to admin context to continue working
              or to start a fresh impersonation.
            </span>
          ) : (
            <span data-testid="impersonation-banner-active-text" className="truncate">
              <strong>Viewing as vendor:</strong>{' '}
              <span className="font-mono">{user?.email || user?.name || user?.id}</span>
              {user?.impersonation_session_id && (
                <span className="hidden md:inline text-amber-700/70 ml-2">
                  · session {String(user.impersonation_session_id).slice(0, 8)}…
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant={expired ? 'destructive' : 'default'}
            className="h-7 text-xs"
            onClick={endImpersonation}
            data-testid="impersonation-banner-end-btn"
          >
            Return to Admin
          </Button>
        </div>
      </div>
    </div>
  );
}
